import type { ReactNode } from 'react'
import { Badge } from './controls/Badge'
import { Menu } from './controls/Menu'
import { cx } from './controls/cx'
import s from './SavedViewSelector.module.css'

export type SavedViewScope = 'user' | 'tenant' | 'solution' | 'persona' | 'system'

export interface SavedViewOption {
  id: string
  name: string
  /** Who supplies it. Absent ⇒ the host has no scope model and the list is flat. */
  scope?: SavedViewScope
}

export interface SavedViewSelectorProps {
  /**
   * The views this caller may offer, already resolved. ELIGIBILITY IS THE
   * HOST'S: which views a deployment declares, which lenses this entity still
   * supports, and which the acting persona may see are all model questions,
   * answered before this component is called. It presents the resolved choices
   * and decides nothing.
   */
  views: SavedViewOption[]
  activeId: string | null
  /**
   * Absent ⇒ THERE IS NO DOOR. The portal reads `core.user_view_pref` and
   * cannot write it, so it showed the view's name as a Badge — which is the
   * honest rendering and is what this component does with no `onSelect`, rather
   * than offering a disabled control that implies a permission problem.
   */
  onSelect?: (id: string) => void
  /**
   * A DOOR WITH NOTHING BEHIND IT — this caller can switch views and the entity
   * has none to switch to. Distinct from omitting `onSelect`, which means there
   * is no door at all: the first renders a disabled control on the toolbar row,
   * the second renders a read-out. A first cut conflated them and the relation
   * bands lost their control entirely, which `ui-live-parity` caught as "saved
   * view and search share the scope row" — a Badge is 15px tall and does not.
   */
  disabled?: boolean
  /** The view pinned as this user's default. Absent with `onPin` ⇒ none is. */
  pinnedId?: string | null
  /** Absent ⇒ pinning is not offered at all. */
  onPin?: (id: string | null) => void
  /** The default this deployment resolved, so the trigger can say which it is. */
  defaultId?: string | null
  /** The active view has unsaved changes. */
  modified?: boolean
  /** The acting persona, for the persona group's heading. */
  personaLabel?: string
  /** The resolver's own diagnostics — an unavailable lens, a fallback. */
  notes?: ReactNode
  /** What the views are OF, for the accessible name: "Saved view for contacts". */
  subject?: string
  className?: string
  /**
   * `field` (default) is the toolbar control, the height of the search beside it.
   * `chip` is the compact view chip a grid plate carries in its header (#2406,
   * leading design: "All addresses ▾") — same menu, same door, smaller seat.
   */
  appearance?: 'field' | 'chip'
  /**
   * How the thing the views are OF can be READ, offered in the same menu
   * (#2406 owner ruling 80629: one switch at the top of Connections, and a
   * relationship's special readings — a map, a timeline — in its own view
   * menu rather than as an icon strip on every plate). Absent ⇒ no section.
   * The trigger names the reading when there are no saved views to name.
   */
  readings?: {
    options: readonly { value: string; label: string }[]
    active: string
    onChange: (value: string) => void
    /** The section heading. Defaults to "Show as". */
    label?: string
  }
  testId?: string
  /**
   * The menu's open state, passed through to `Menu` (Radix). Optional: absent,
   * the picker is uncontrolled exactly as before. A gallery or a design capture
   * uses it to show the open menu without driving the trigger (#2406
   * kit-defects-b — the Claude Design sync could only render it closed). A
   * disabled or door-less picker renders no menu, so these do nothing there.
   */
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

const SCOPES: SavedViewScope[] = ['user', 'tenant', 'solution', 'persona', 'system']
const HEADING: Record<SavedViewScope, string> = {
  user: 'My views', tenant: 'Tenant', solution: 'Solution',
  persona: 'Persona', system: 'Standard',
}

/**
 * THE SAVED-VIEW PICKER (#2406 §4).
 *
 * ── the three shapes it replaces ───────────────────────────────────────────
 * `ListView` drew a button plus a hand-rolled popover, grouped by scope and
 * carrying a pin star. `RelationBand` and `RelationCascade` each drew a bare
 * Radix `<Select>` — byte-for-byte identical to one another, and diverging from
 * each other in one place that was not cosmetic: only one of them reset the
 * local sort, filter and hidden columns when the view changed. `PortalGrid`
 * drew a `Badge`, because the portal has no door onto the preference.
 *
 * Three widgets for one job, and the one that looked most finished was the
 * least accessible: the popover had no `role`, no roving focus, no type-ahead,
 * no `aria-checked` on the selected row, and no focus return on Escape. The two
 * Selects had all of that by inheritance and were the ones nobody noticed.
 *
 * So this is `Menu` — Radix — with `RadioItem`s. One-of-a-set is what a view
 * picker IS, and `role="menuitemradio"` says "3 of 7" where a tick glyph said
 * nothing at all.
 *
 * ── pinning is not a control inside a row ──────────────────────────────────
 * `ListView` put a pin `<button>` inside each row's `<button>`: invalid HTML,
 * which is why it needed `stopPropagation` to work, and which a screen reader
 * resolves by flattening or by dropping one of the two. A menu item cannot hold
 * a second control either, so pinning became one `CheckItem` at the foot,
 * naming the view it acts on.
 *
 * That is a real behaviour change and it is deliberate: you can no longer pin a
 * view you are not on. The trade is that pinning is reachable from a keyboard
 * for the first time, and that the row is one control rather than two
 * overlapping ones. If pinning an unopened view turns out to matter, it wants
 * its own surface rather than a nested button.
 */
export function SavedViewSelector({
  views, activeId, onSelect, disabled, pinnedId, onPin, defaultId, modified,
  personaLabel, notes, subject, className, appearance = 'field', testId = 'view-tabs', readings,
  open, defaultOpen, onOpenChange,
}: SavedViewSelectorProps) {
  const active = views.find((v) => v.id === activeId) ?? views[0]
  const label = `Saved view${subject ? ` for ${subject}` : ''}`

  // No door ⇒ no control. The portal's shape, and the honest one.
  if (!onSelect) {
    return active
      ? <Badge tone="neutral" data-testid={testId}>{active.name}</Badge>
      : null
  }

  const triggerButton = (
    <button type="button" className={cx(s.trigger, appearance === 'chip' && s.chip, className)}
      aria-label={label} disabled={disabled}>
      {active?.scope === 'user' && <span className={s.mine} data-view-scope="user" aria-hidden="true">●</span>}
      <span className={s.name}>
        {active?.name ?? readings?.options.find((o) => o.value === readings.active)?.label ?? 'View'}
      </span>
      {active != null && active.id === defaultId && <Badge tone="brand">default</Badge>}
      {/* MODIFIED, in words (#2375): a dot said "something" to a sighted reader
          and nothing to a screen reader. The warn tint is the amber the design
          asks for; the word carries the state. */}
      {modified && <span data-view-modified=""><Badge tone="warn">modified</Badge></span>}
      <span className={s.caret} aria-hidden="true">▾</span>
    </button>
  )

  /* A disabled trigger opens nothing, so it is not wrapped in a Menu at all —
     a `Menu` whose content can never be reached is a portal and a listener for
     no reason, and Radix would still bind Escape and outside-click to it. */
  if (disabled) return triggerButton

  const grouped = SCOPES
    .map((scope) => [scope, views.filter((v) => v.scope === scope)] as const)
    .filter(([, list]) => list.length > 0)
  const unscoped = views.filter((v) => v.scope == null)

  const rows = (list: readonly SavedViewOption[]) =>
    list.map((v) => <Menu.RadioItem key={v.id} value={v.id} testId={`view-${v.id}`}>{v.name}</Menu.RadioItem>)

  return (
    <Menu
      align="start"
      testId={testId}
      trigger={triggerButton}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {notes}
      {views.length > 0 && <Menu.RadioGroup value={active?.id ?? ''} onValueChange={onSelect}>
        {unscoped.length > 0 && rows(unscoped)}
        {grouped.map(([scope, list], i) => (
          <div key={scope}>
            {(i > 0 || unscoped.length > 0) && <Menu.Separator />}
            <Menu.Label>{scope === 'persona' && personaLabel ? `${personaLabel} — persona` : HEADING[scope]}</Menu.Label>
            {rows(list)}
          </div>
        ))}
      </Menu.RadioGroup>}
      {readings && readings.options.length > 1 && (
        <>
          {views.length > 0 && <Menu.Separator />}
          <Menu.Label>{readings.label ?? 'Show as'}</Menu.Label>
          <Menu.RadioGroup value={readings.active} onValueChange={readings.onChange}>
            {readings.options.map((o) => (
              <Menu.RadioItem key={o.value} value={o.value} testId={`reading-${o.value}`}>{o.label}</Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
        </>
      )}
      {onPin && active && (
        <>
          <Menu.Separator />
          <Menu.CheckItem
            checked={pinnedId === active.id}
            testId="pin-view"
            onSelect={(next) => onPin(next ? active.id : null)}
          >
            Pin “{active.name}” as my default
          </Menu.CheckItem>
        </>
      )}
    </Menu>
  )
}
